import { UserCircle } from "@phosphor-icons/react";
import { useState } from "react";
import type { FormEvent } from "react";

import {
  useCancelPersonalRequest, usePersonalRequests, useRecordPersonalRequest,
} from "../api/personalRequests";
import type { RequestKind } from "../api/personalRequests";
import { useReader } from "../api/profile";
import { Loaded, WriteFailed } from "../components/Loaded";
import { Panel } from "../components/Panel";
import { InfoPopover, PageHeaderCard } from "../components/ui";
import { YouLinks } from "./settingsLinks";
import { PERSONAL_REQUEST_KINDS, personalRequestKindWords, personalRequestStateWords } from "../lib/customerWords";
import { instantDate } from "../lib/format";
import { RequireSession } from "../shell/RequireSession";
import { ScreenFrame } from "./ScreenFrame";
import styles from "./Screen.module.css";
import { useScreen } from "./useScreen";

/** The day a request was recorded, in the reader's zone. **Its own component** so the preferences are
 * asked for only when there is a request to date, never for an empty list. */
function RecordedOn({ at }: { at: string }) {
  const reader = useReader();
  return <>{instantDate(at, reader)}</>;
}

function RequestBody() {
  const screen = useScreen();
  const [offset, setOffset] = useState(0);
  const [kind, setKind] = useState<RequestKind | "">("");
  const [clientId, setClientId] = useState(() => crypto.randomUUID());
  const [attempted, setAttempted] = useState(false);
  const history = usePersonalRequests(offset);
  const recorded = useRecordPersonalRequest();
  const cancelled = useCancelPersonalRequest();

  async function submit(event?: FormEvent) {
    event?.preventDefault();
    if (!kind) return;
    setAttempted(true);
    try {
      await recorded.mutateAsync({ kind, client_request_id: clientId });
      setKind("");
      setClientId(crypto.randomUUID());
      setAttempted(false);
      setOffset(0);
    } catch {
      return;
    }
  }

  return (
    <ScreenFrame screen={screen} title="Your data requests" crumb="your account’s intake records" bodyScrolls>
      <PageHeaderCard
        icon={UserCircle}
        title="Your data requests"
        label="Record a request about your own data"
      />
      <YouLinks current="/privacy-requests" search={screen} />
      <Panel title="Record a personal request" column="1 / 13" action={<InfoPopover infoKey="legacy.personal-requests" />}>
        <p className={styles.note}>
          This records your request kind for your account only. It has not been assessed or routed. This
          form does not accept requests from non-users or representatives.
        </p>
        <form onSubmit={(event) => void submit(event)}>
          <label htmlFor="personal-request-kind">Request kind</label>
          <select id="personal-request-kind" value={kind} disabled={recorded.isPending || attempted}
            onChange={(event) => setKind(event.target.value as RequestKind | "")} required>
            <option value="">Choose what you want</option>
            {PERSONAL_REQUEST_KINDS.map((value) => (
              <option key={value} value={value}>{personalRequestKindWords(value)}</option>
            ))}
          </select>
          <button type="submit" disabled={!kind || recorded.isPending}>Record request</button>
        </form>
        <WriteFailed error={recorded.error} of="Personal request" retrySafe onRetry={() => void submit()} />
        {recorded.data ? <p role="status">Request recorded. No action has been taken.</p> : null}
      </Panel>
      <Panel title="Your recorded requests" column="1 / 13">
        <WriteFailed error={cancelled.error} of="Request cancellation" />
        <Loaded query={history} of="Your personal requests">
          {(page) => <>
            {page.requests.length === 0 ? <p data-testid="requests-empty">No personal requests recorded.</p> :
              <ul data-testid="personal-requests">{page.requests.map((request) => <li key={request.id}>
                <p>{personalRequestKindWords(request.kind)} · {personalRequestStateWords(request.state)}</p>
                <p>Recorded <RecordedOn at={request.received_at} />. This is the day it was recorded, not a legal response clock.</p>
                {request.state === "recorded" ? <button type="button" disabled={cancelled.isPending}
                  onClick={() => cancelled.mutate(request.id)}>Cancel this intake record</button> :
                  <p>This does not reverse another action or delete retained records.</p>}
              </li>)}</ul>}
            <button type="button" disabled={offset === 0} onClick={() => setOffset(offset - 100)}>Newer requests</button>
            <button type="button" disabled={!page.has_more} onClick={() => setOffset(offset + 100)}>Older requests</button>
          </>}
        </Loaded>
      </Panel>
    </ScreenFrame>
  );
}

export function PersonalRequestsScreen() {
  return <RequireSession><RequestBody /></RequireSession>;
}
